import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { groupStudentsByCourseAndBatch, formatScheduleType } from '../../utils/studentAttendanceGrouping';

const PRINT_STYLES = `
@media print {
  @page { size: A4 portrait; margin: 16mm 14mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #student-attendance-print table { page-break-inside: auto; }
  #student-attendance-print tr { page-break-inside: avoid; break-inside: avoid; }
  #student-attendance-print .print-course-block:not(:first-child) { page-break-before: always; break-before: page; }
  /* Keep the tail of the last batch's table together with the sign-off block
     so the signature never lands alone at the top of a fresh page. */
  #student-attendance-print .print-signoff-group { page-break-inside: avoid; break-inside: avoid; }
}
`;

function pctBadgeStyle(pct) {
  if (pct === null || pct === undefined) return { background: '#f3f4f6', color: '#6b7280' };
  if (pct >= 75) return { background: '#dcfce7', color: '#15803d' };
  if (pct >= 50) return { background: '#fef3c7', color: '#b45309' };
  return { background: '#fee2e2', color: '#b91c1c' };
}

function BatchAttendanceTable({ batch }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">
          {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
        </p>
        <span className="rounded px-2 py-0.5 text-xs font-bold" style={pctBadgeStyle(batch.avgPercentage)}>
          {batch.avgPercentage === null ? '—' : `${batch.avgPercentage}%`} average
        </span>
      </div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border border-gray-400 bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">#</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Student Name</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Student ID</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Reg. No.</th>
            <th className="border border-gray-400 px-2 py-1 text-center">Held</th>
            <th className="border border-gray-400 px-2 py-1 text-center">Attended</th>
            <th className="border border-gray-400 px-2 py-1 text-center">Absent</th>
            <th className="border border-gray-400 px-2 py-1 text-center">%</th>
          </tr>
        </thead>
        <tbody>
          {batch.students.map((s, idx) => (
            <tr key={`${s.studentId}-${s.registrationNumber}-${s.batchId}`}>
              <td className="border border-gray-400 px-2 py-1">{idx + 1}</td>
              <td className="border border-gray-400 px-2 py-1">{s.studentName || '—'}</td>
              <td className="border border-gray-400 px-2 py-1">{s.studentId || '—'}</td>
              <td className="border border-gray-400 px-2 py-1">{s.registrationNumber || '—'}</td>
              <td className="border border-gray-400 px-2 py-1 text-center">{s.totalClasses}</td>
              <td className="border border-gray-400 px-2 py-1 text-center">{s.attendedClasses}</td>
              <td className="border border-gray-400 px-2 py-1 text-center">{s.absentClasses}</td>
              <td className="border border-gray-400 px-2 py-1 text-center font-semibold" style={pctBadgeStyle(s.overallPercentage)}>
                {s.overallPercentage === null ? '—' : `${s.overallPercentage}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StudentAttendancePrintView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [minPercentage, setMinPercentage] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const batchIdsParam = searchParams.get('batchIds') || 'all';
  const minPercentageParam = searchParams.get('minPercentage');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        const accessData = accessRes?.data || {};
        const isSuperAdmin = !!accessData.isSuperAdmin;
        const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';
        const accessibleCourseIds = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];

        const accessibleParam = isSuperAdmin || isManagement
          ? 'all'
          : accessibleCourseIds.map((id) => Number(id)).filter((num) => Number.isFinite(num) && num > 0);

        const resp = await axiosInstance.get('/admin/dashboard/student-attendance', {
          params: {
            accessibleCourses: accessibleParam,
            batchIds: batchIdsParam,
            minPercentage: minPercentageParam || undefined,
          },
        });
        if (!isMounted) return;

        const data = resp?.data || {};
        setStudents(data.students || []);
        setMinPercentage(data.minPercentage ?? null);
        setGeneratedAt(new Date().toISOString());
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load student attendance report');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the print dialog once the document has rendered
  useEffect(() => {
    if (loading || error) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error]);

  const groupedByCourse = useMemo(() => groupStudentsByCourseAndBatch(students), [students]);

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Student Attendance Report</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div id="student-attendance-print" className="mx-auto max-w-4xl bg-white p-10 shadow-sm print:shadow-none">
          {loading ? (
            <p className="text-center text-gray-500">Loading…</p>
          ) : error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : (
            <>
              {groupedByCourse.length === 0 && (
                <p className="text-center text-sm text-gray-500">No students match the applied filters.</p>
              )}
              {groupedByCourse.map((course) => {
                const courseStudentCount = course.batches.reduce((sum, b) => sum + b.students.length, 0);
                const courseBatchNames = course.batches
                  .map((b) => b.batchInternalName || b.whatsappGroupName || b.batchName || 'Batch')
                  .join(', ');
                return (
                <div key={course.courseId ?? course.courseName} className="print-course-block">
                  {/* Letterhead — repeated per course so each course prints as its own standalone report */}
                  <div className="border-b-2 border-gray-800 pb-4 text-center">
                    <h1 className="text-2xl font-black tracking-wide text-gray-900">ESOFT METRO CAMPUS BATTICALOA</h1>
                    <p className="mt-0.5 text-xs uppercase tracking-widest text-gray-500">Academic Affairs Division</p>
                  </div>

                  <div className="mt-5 text-center">
                    <span className="inline-block rounded-sm border border-gray-800 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-800">
                      Official Notice
                    </span>
                    <h2 className="mt-3 text-xl font-bold text-gray-900">Student Attendance Report</h2>
                    <p className="mt-1 text-base font-semibold text-gray-800">
                      {course.courseShortName || course.courseName || 'Unknown Course'}
                      {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1 border border-gray-300 px-4 py-3 text-xs text-gray-700">
                    <div><span className="font-semibold">Generated:</span> {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}</div>
                    <div className="col-span-2">
                      <span className="font-semibold">Batches covered ({course.batches.length}):</span> {courseBatchNames || '—'}
                    </div>
                    <div>
                      <span className="font-semibold">Minimum attendance filter:</span>{' '}
                      {minPercentage !== null && minPercentage !== undefined ? `${minPercentage}% and above` : 'None (all students shown)'}
                    </div>
                    <div><span className="font-semibold">Students listed:</span> {courseStudentCount}</div>
                  </div>

                  <div className="mt-6 space-y-5">
                    {course.batches.slice(0, -1).map((batch) => (
                      <BatchAttendanceTable key={batch.batchId} batch={batch} />
                    ))}

                    {/* Last batch's table is grouped with the sign-off block so the
                        signature never ends up alone at the top of a fresh page. */}
                    <div className="print-signoff-group space-y-5">
                      {course.batches.length > 0 && (
                        <BatchAttendanceTable
                          key={course.batches[course.batches.length - 1].batchId}
                          batch={course.batches[course.batches.length - 1]}
                        />
                      )}

                      <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                        </div>
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Head of Department — Signature &amp; Date</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}
